import {
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeIcon,
  TePagination,
  TePlaceholderContainer,
  TePopover,
  TeSearch,
  TeText,
} from '@topcon/tuicl-react';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { BusinessPartnerBody } from '../../api/generated/pm';
import { PageWrapper } from '../../utils/styling';
import { useOrganizations } from './organizations.queries';
import Organization, { formatAddress } from './Organization';
import {
  buildOrganizationHierarchy,
  flattenOrganizationHierarchy,
  getOrganizationAuthURL,
} from './utils';

interface StoredOrganization {
  id: string;
  extId: string;
  name: string;
  address: string;
  type?: string;
}

const localStorageKey = 'tn-organization-recently-used';

const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 16px 8px;
`;

const PlaceholderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 75px;
  font-style: italic;
`;

const getStoredRecents = (): BusinessPartnerBody[] => {
  const stored = localStorage.getItem(localStorageKey);
  if (!stored) return [];
  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return (parsed as StoredOrganization[]).map((item) => ({
      id: item.id ?? '',
      extId: item.extId,
      name: item.name,
      companyAddress: {
        addressLineOne: item.address,
        addressLineTwo: '',
        city: '',
        stateProvinceRegion: '',
        zipPostalCode: '',
        country: '',
      },
      createdAt: new Date().toISOString(),
      type: item.type ?? '',
    }));
  } catch {
    return [];
  }
};

const pageLimit = 99;

function Organizations() {
  const { t } = useTranslation('organizations');
  const [search, setSearch] = useState('');
  const [searchForOrg, setSearchForOrg] = useState<string | null>(null);
  const [recentOrgs, setRecentOrgs] = useState<BusinessPartnerBody[]>(getStoredRecents());
  const [selectedPage, setSelectedPage] = useState(1);

  const isSearchActive = searchForOrg !== null;

  const { data: organizationsResponse, isLoading } = useOrganizations({
    search: isSearchActive ? searchForOrg : undefined,
    isEnabled: isSearchActive,
  });

  const organizations = useMemo(
    () => organizationsResponse?.items ?? [],
    [organizationsResponse?.items]
  );

  const hierarchicalOrganizations = useMemo(() => {
    const hierarchy = buildOrganizationHierarchy(organizations);
    return flattenOrganizationHierarchy(hierarchy);
  }, [organizations]);

  const totalPages = Math.max(1, Math.ceil(hierarchicalOrganizations.length / pageLimit));

  const pagedOrganizations = useMemo(
    () => hierarchicalOrganizations.slice((selectedPage - 1) * pageLimit, selectedPage * pageLimit),
    [hierarchicalOrganizations, selectedPage]
  );

  const runSearch = useCallback(() => {
    setSelectedPage(1);
    if (search.length < 4) setSearchForOrg(null);
    else setSearchForOrg(search);
  }, [search]);

  const storeAsRecent = useCallback(
    (organization: BusinessPartnerBody) => {
      const filtered = recentOrgs.filter((o) => o.extId !== organization.extId);
      const newRecent = [organization, ...filtered].slice(0, 3);
      setRecentOrgs(newRecent);

      const storageFormat = newRecent.map((org) => ({
        extId: org.extId,
        name: org.name,
        address: formatAddress(org.companyAddress),
        id: org.id ?? '',
        type: org.type,
      }));

      try {
        localStorage.setItem(localStorageKey, JSON.stringify(storageFormat));
      } catch {
        // localStorage may be full — recents won't persist but the UI still works
      }
    },
    [recentOrgs]
  );

  const openServiceAgreement = useCallback(
    (organization: BusinessPartnerBody) => {
      const url = getOrganizationAuthURL(organization.id);
      if (!url) return;
      storeAsRecent(organization);
      window.location.replace(url);
    },
    [storeAsRecent]
  );

  const renderSearch = useCallback(() => {
    return (
      <TeContainerSlim fill>
        <TeSearch
          value={search}
          techange={(e) => setSearch(e.target.value)}
          placeholder={t('searchOrganizationPlaceholder')}
          clearable
          size="s"
          valid={search.length >= 4 || search.length === 0}
          teblur={runSearch}
          teclick={runSearch}
          teclear={() => {
            setSelectedPage(1);
            setSearchForOrg(null);
          }}
          tekeydown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          postfixIcon="arrow_forward"
          testId="organizations-search-input"
        />
      </TeContainerSlim>
    );
  }, [runSearch, search, t]);

  const renderHeader = useCallback(
    () => (
      <TeContainerSlim justifyContent="center" fill>
        <TeContainerSlim column alignItems="center" padding={['xl', 's', 'l', 's']} gap="xl">
          <TeContainerSlim column gap="xs" alignItems="center">
            <TeText size="xl" weight="bold">
              {t('searchForOrganization')}
            </TeText>
            <TeContainerSlim gap="xs" alignItems="center">
              <TeText>{t('searchByCompanyNameOrCustomerNumber')}</TeText>
              <TeIcon name="info" popkey="org-info-icon" />
              <TePopover
                variant="secondary"
                placement="bottom"
                for="org-info-icon"
                operation="hover"
              >
                <TeContainer width="256">
                  <TeText size="s" color="text.inverse">
                    {t('searchByCompanyNameOrCustomerNumberInfo')}
                  </TeText>
                </TeContainer>
              </TePopover>
            </TeContainerSlim>
          </TeContainerSlim>
          {renderSearch()}
        </TeContainerSlim>
      </TeContainerSlim>
    ),
    [renderSearch, t]
  );

  const renderOrganizations = useCallback(() => {
    if (!isSearchActive || isLoading) return null;
    return pagedOrganizations.map((org) => (
      <Organization
        key={org.extId}
        organization={org}
        openServiceAgreement={openServiceAgreement}
        level={org.level}
        isLastChild={org.isLastChild}
      />
    ));
  }, [isLoading, isSearchActive, openServiceAgreement, pagedOrganizations]);

  const renderRecentOrganizations = useCallback(() => {
    if (isLoading || recentOrgs.length === 0 || (organizations.length > 0 && !isLoading))
      return null;
    return (
      <>
        <TeDivider
          text={t('mostRecentlyUsed')}
          textPlacement="center"
          dividerColor="border.primary"
          textColor="text.primary"
        />
        {recentOrgs.map((org) => (
          <Organization
            key={org.extId}
            organization={org}
            openServiceAgreement={openServiceAgreement}
          />
        ))}
      </>
    );
  }, [isLoading, openServiceAgreement, organizations.length, recentOrgs, t]);

  const getPlaceholderText = useCallback(() => {
    if (!isSearchActive) return t('organizationsWillAppearAfterSearch');
    else if (isLoading) return t('loadingOrganizations');
    else return t('noMatchingOrganizations');
  }, [isLoading, isSearchActive, t]);

  const getPlaceholderLoading = useCallback(() => {
    if (!isSearchActive) return false;
    else return isLoading;
  }, [isLoading, isSearchActive]);

  const renderPlaceholder = useCallback(() => {
    if (hierarchicalOrganizations.length > 0 && !isLoading) return null;
    return (
      <PlaceholderWrapper>
        <TePlaceholderContainer text={getPlaceholderText()} loader={getPlaceholderLoading()} />
      </PlaceholderWrapper>
    );
  }, [getPlaceholderLoading, getPlaceholderText, isLoading, hierarchicalOrganizations.length]);

  return (
    <TeContainer grow column overflow="hidden" alignItems="center">
      <PageWrapper>
        <TeContainerSlim
          column
          backgroundColor="white"
          grow
          borderColor="border.secondary"
          padding="xs"
          overflow="hidden"
          borderRadius="m"
        >
          {renderHeader()}
          <TeContainer
            backgroundColor="background.secondary"
            borderColor="border.secondary"
            borderRadius="m"
            gap="s"
            column
            fill
            overflow="hidden"
          >
            {renderPlaceholder()}
            <ScrollContainer>
              {renderOrganizations()}
              {renderRecentOrganizations()}
            </ScrollContainer>
            {totalPages > 1 && (
              <TePagination
                pages={totalPages}
                page={selectedPage}
                techange={(e) => setSelectedPage(e.page)}
              />
            )}
          </TeContainer>
        </TeContainerSlim>
      </PageWrapper>
    </TeContainer>
  );
}

export default Organizations;
